import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Typography, List, ListItem, ListItemIcon, ListItemText, Checkbox } from '@mui/material';
import { useCreateInvoiceItem } from '../../../hooks/invoice/useCreateInvoiceItem';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useHistoryInventoryTransactions } from '../../../hooks/inventory/useHistoryInventoryTransactions';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';

export default function InvoiceTransactionItemsForm({
  invoice = null,
  onInvoiceItemSaveSuccess = null,
  open,
  onClose,
}) {
  const { t } = useTranslation("common");

  const {
    transactions,
    loading,
    loadMore,
    searchFunction,
    resetParent,
  } = useHistoryInventoryTransactions(
    invoice.history?.id,
    invoice.historyEntry?.id || null
  );

  // Track selected transaction IDs
  const [items, setItems] = React.useState([]);

  const { create } = useCreateInvoiceItem(onInvoiceItemSaveSuccess);
  const { showSnackbar } = useSnackbar();

  React.useEffect(() => {
    // reset field when there are changes (e.g. when opening edit)
    setItems([]);
  }, [open]);

  const handleSave = async () => {
    try {
      const selectedItems = transactions?.data
        ?.filter((t) => items.includes(t.id))
        ?.map((transaction) => ({
          invoice_id: invoice.id,
          name: transaction.item.name,
          quantity: transaction.quantity,
          unit_price: transaction.unit_selling_price,
        }));
      await create(selectedItems);
      showSnackbar(t("invoice.items.messages.create_success"), "success");
      setItems([]);
      onClose();
    } catch (err) {
      console.log("err.response", err.response);
      showSnackbar(t("invoice.items.messages.save_fail"), "error");
    }
  };

  const handleCancel = () => {
    setItems([]);
    onClose();
  };

  return (
    <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
      <DialogTitle>Add Items From Transactions</DialogTitle>
      <DialogContent sx={{minHeight: 200}} dividers>
        <List>
          {transactions?.data?.map((transaction) => {
            const isChecked = items.includes(transaction.id);

            const handleCheckboxChange = (event) => {
              if (event.target.checked) {
                setItems((prev) => [...prev, transaction.id]);
              } else {
                setItems((prev) =>
                  prev.filter((id) => id !== transaction.id)
                );
              }
            };

            return (
              <ListItem
                key={"transaction" + transaction.id}
                sx={{ py: 0 }}
                alignItems="flex-start"
              >
                <ListItemIcon>
                  <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" sx={{ maxWidth: "90%" }}>
                      {transaction.item.name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" sx={{ my: 0, maxWidth: "90%" }}>
                      {`qty ${removeZeroDecimals(transaction.quantity)} x price ${numberToCurrencyFormat(transaction.unit_selling_price)}`}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
          {transactions?.data.length === 0 ?
          <ListItem sx={{ justifyContent: "center" }}>
            <Typography component="span" variant="body2" color="text.primary">
              no transactions found
            </Typography>
          </ListItem>
          : null }
        </List>
      </DialogContent>

      <DialogActions>
        <Button disabled={items.length === 0} onClick={handleSave}>Add Selected Items</Button>
        <div style={{flex: '1 0 0'}} />
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}